import { createContext } from "react";
import { doNothing } from "@edgetier/utilities";
import { IInteractionModalContext } from "./context.types";

/** Default context values for the interaction modal when no provider is present */
const defaultInteractionModalContext: IInteractionModalContext = {
    user: {
        data: null,
        isLoading: false,
    },
    interaction: {
        data: null,
        isLoading: false,
    },
    interactionWritingIssues: {
        data: null,
        isLoading: false,
    },
    interactionMessages: {
        data: null,
        isLoading: false,
        highlightedMessageId: null,
        setHighlightedMessageId: doNothing,
        currentAudioMessageId: null,
        setCurrentAudioMessageId: doNothing,
        isUserControllingScroll: false,
        setIsUserControllingScroll: doNothing,
        currentAudioMessagePosition: null,
        setCurrentAudioMessagePosition: doNothing,
        interactionActiveSentiment: {},
        setInteractionActiveSentiment: doNothing,
    },
    interactionMessagesAgents: {
        data: null,
        isLoading: false,
    },
    interactionMessagesTagGroups: {
        data: null,
        isLoading: false,
    },
    interactionDetails: {
        data: null,
        isLoading: false,
    },
    interactionsFromSameContact: undefined,
    getContactUrl: async () => Promise.resolve("#"),
    getViewReviewUrl: async () => Promise.resolve("#"),
    onStartReview: async () => Promise.resolve(),
    onSentimentToggle: undefined,
    interactionNavigation: undefined,
    permissions: {
        interactionSummary: true,
        viewReviews: true,
        startReviews: true,
    },
    sidebar: {
        sections: [],
    },
};

/** The React context object for the interaction modal */
const InteractionModalContext = createContext<IInteractionModalContext>(defaultInteractionModalContext);

/**
 * Provider component that wraps children with interaction modal context values.
 * @param {React.ReactNode | Function} props.children - The child elements or render function to wrap with context.
 * @param {Partial<IInteractionModalContext>} props - Additional context values to override the defaults.
 * @returns {JSX.Element} The InteractionModalProvider component.
 */
const InteractionModalProvider = ({
    children,
    ...otherProps
}: Partial<IInteractionModalContext> & {
    children: React.ReactNode | ((context: IInteractionModalContext) => React.ReactNode);
}) => {
    // This provider would typically wrap children components and provide context values
    const contextValue = { ...defaultInteractionModalContext, ...otherProps };
    return (
        <InteractionModalContext.Provider value={contextValue}>
            {typeof children === "function" ? children(contextValue) : children}
        </InteractionModalContext.Provider>
    );
};

export { InteractionModalContext, InteractionModalProvider };
